import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { BiddingIntradayOrderStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  Spacer,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownToBracketRegular } from '@enechain/ecloud-icons'

import { ExecutionAreaSummaryTable } from '~/components/bidding/common/ExecutionAreaSummaryTable'
import { computeExecutionAreaSummary } from '~/components/bidding/common/executionSummary'
import { RegionLoading } from '~/components/common/RegionLoading'
import { iconSize } from '~/constants/iconSize'
import {
  convertListIntradayResponse,
  useListBiddingIntradayOrders,
} from '~/hooks/bidding/useListBiddingIntradayOrders'
import { useNowByMinute } from '~/hooks/useNowByMinute'
import { downloadCsv } from '~/libs/downloadCsv'
import { convertAreaLabel } from '~/models/area'
import { formatDateValue, toProtoDate } from '~/models/dateTime'
import { komaNumberToTime } from '~/models/komaToTime'
import { ExecutionDetailTable } from '~/pages/IntradayBidding/components/ExecutionDetailTable'
import { ExecutionResultKpiGrid } from '~/pages/IntradayBidding/components/ExecutionResultKpiGrid'
import {
  buildExecutionCsv,
  isExecuted,
  sortExecutionRows,
} from '~/pages/IntradayBidding/models/executionResult'
import type { IntradaySideFilter } from '~/pages/IntradayBidding/models/globalFilters'
import { computeIntradayExecutionKpi } from '~/pages/IntradayBidding/models/intradayExecutionKpi'
import { toExecutionSummaryRecords } from '~/pages/IntradayBidding/models/intradayExecutionSummary'

/** 受渡日 CompactCivilDateField の id (= label htmlFor / test の到達点)。 */
type Props = {
  /**
   * AC-BIN-102 全体フィルタの受渡日 (入札管理・板発注と共有)。 受渡日を選ぶ UI は本タブには
   * 無く、 4 タブ共通の全体の条件 ([[IntradayFilterToolbar]]) のカードの中の
   * [[IntradayDeliveryDateControl]] が持つ。
   */
  targetDate: Date
  /** AC-BIN-102 全体フィルタのエリア (= 選択中エリアの実集合、 [[IntradayAreaMultiSelect]] と同じ契約)。 */
  selectedAreas: Set<AreaCode>
  /** AC-BIN-102 全体フィルタの売買区分 (全体の条件〔[[IntradayFilterToolbar]]〕で選ぶ)。 */
  side: IntradaySideFilter
}

/**
 * 約定結果タブ (= design `intraday-result.html`)。
 *
 * 受渡日=サーバ絞り(タブ自前クエリ + 5 秒 polling で自動更新)、 売買/エリア=client 絞り。
 * KPI / エリア×売買集計 / 約定明細 は、 受渡日・エリア・売買の全体フィルタ (AC-BIN-102) に連動する。
 * 約定明細は注文単位の集計約定 (= 約定済のみ)。 約定ごとの内訳は AC-BIN-022。
 *
 * 受渡日・エリア・売買区分は AC-BIN-102 の全体フィルタ (親 `IntradayBiddingPage` が
 * 持つ) を props 経由で受け取る controlled component。 タブ自前の state は持たない
 * (= 入札管理・板発注タブと選択状態を共有する)。
 */
export const ExecutionResultTab = ({
  targetDate,
  selectedAreas,
  side,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const targetDateFilter = useMemo(() => toProtoDate(targetDate), [targetDate])

  // 受渡日=サーバ絞り。 error はインライン表示したいので throwOnError:false。
  // 約定明細の JEPX約定番号の列 (AC-BIN-022a) のため約定番号を求める。
  const { data, isLoading, isError } = useListBiddingIntradayOrders(
    targetDateFilter,
    undefined,
    { throwOnError: false, includeJepxExecutionNos: true },
  )

  const rows = useMemo(
    () => (data !== undefined ? convertListIntradayResponse(data) : []),
    [data],
  )

  // AC-BIN-102: エリア・売買区分は全体フィルタ (= 入札管理・入札履歴タブで選択した値) を
  // KPI (AC-BIN-021)・エリア × 売買 別 集計 (AC-BIN-022b)・約定明細のすべてに適用する。
  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          selectedAreas.has(r.areaCode) &&
          (side === 'all' ||
            r.direction === (side === 'buy' ? Side.BUY : Side.SELL)),
      ),
    [rows, selectedAreas, side],
  )

  // 約定明細テーブル行 = 約定済のみ (件数表示も同じ)、 既定並びでソート。
  // KPI カードと集計表の母集合はこれとは**別**で、 Submit 済 かつ GC 済みコマの全注文
  // (未約定・失効・取消を含む。 `toExecutionSummaryRecords`)。
  const detailRows = useMemo(
    () => sortExecutionRows(filteredRows.filter((r) => isExecuted(r))),
    [filteredRows],
  )

  // エリア × 売買 別 集計 (AC-BIN-022b) の母集合は **Submit 済 × GC 済みコマの全注文**
  // (= 約定済に絞らない)。 未約定・失効を落とすと約定率が常に 100% になり機会損失が
  // 指標から消える (`mock-app/docs/domain-knowledge.md`)。 母集合・分母の規則は
  // `toExecutionSummaryRecords` が持ち、 集計の実体は spot と共通の
  // `computeExecutionAreaSummary`。 時間前固有なのは 写像 だけ。
  //
  // GC 判定に使う現在時刻は 1 分ごとに更新する。
  // mount 時刻で固定すると、 タブを開いたままコマの GC を跨いだ時に新しく確定した
  // コマが集計に入ってこない。
  const now = useNowByMinute()
  // KPI カード (AC-BIN-021) と集計表 (AC-BIN-022b) は同じ母集合で算出する
  // (AC-BIN-022b は母集合を AC-BIN-021 の約定率算出式と同一と定める)。
  const summaryRecords = useMemo(
    () => toExecutionSummaryRecords(filteredRows, now),
    [filteredRows, now],
  )
  const summary = useMemo(
    () => computeExecutionAreaSummary(summaryRecords),
    [summaryRecords],
  )
  const kpi = useMemo(
    () => computeIntradayExecutionKpi(summaryRecords),
    [summaryRecords],
  )

  // 約定ステータス列 (AC-BIN-022a) は「約定 / 部分約定」 の約定の有無の軸で、
  // ステータス列 (AC-BIN-035 の「全約定」 等) とは別の語彙を持つ。 ステータス列の
  // key を流用すると片方の文言変更に引きずられるため、 専用 key を使う。
  const statusLabel = (status: BiddingIntradayOrderStatus): string => {
    if (status === BiddingIntradayOrderStatus.ALL_MATCHED) {
      return t('page:biddingIntraday.executionResult.detail.statusValue.traded')
    }
    if (status === BiddingIntradayOrderStatus.PARTIAL_MATCHED) {
      return t(
        'page:biddingIntraday.executionResult.detail.statusValue.partial',
      )
    }
    return t('page:biddingIntraday.orderStatus.unspecified')
  }

  const handleCsvDownload = (): void => {
    const csv = buildExecutionCsv(detailRows, {
      timeCode: komaNumberToTime,
      area: convertAreaLabel,
      side: (direction) =>
        direction === Side.BUY
          ? t('page:biddingIntraday.executionResult.side.buy')
          : t('page:biddingIntraday.executionResult.side.sell'),
      status: statusLabel,
    })
    downloadCsv(
      `時間前約定明細_${formatDateValue(targetDate).replaceAll('-', '')}.csv`,
      csv,
    )
  }

  return (
    <VStack align="stretch" gap={3}>
      {/* 受渡日・エリア・売/買 は全体フィルター (AC-BIN-102 / AC-BIN-131) として画面の最上部の全体の条件
          ([[IntradayFilterToolbar]]) に置くため、 このタブに絞り込みのカードは無い。 行数は右寄せで出す */}
      <Flex justifyContent="flex-end">
        <Text
          color="Text.highEmphasis"
          fontSize="md"
          css={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {t('page:biddingIntraday.executionResult.rowCount', {
            count: detailRows.length,
          })}
        </Text>
      </Flex>

      <RegionLoading isLoading={isLoading && data === undefined}>
        {isLoading && data === undefined ? null : isError ? (
          <Box padding={4} textAlign="center">
            <Text color="Text.lowEmphasis">
              {t('page:biddingIntraday.executionResult.loadError')}
            </Text>
          </Box>
        ) : (
          <>
            <ExecutionResultKpiGrid kpi={kpi} />

            {/* エリア × 売買 約定集計 (= 見出し付き静的セクション、 [DS差異]) */}
            <Box
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
              backgroundColor="Surface.primary"
            >
              <Box paddingX={4} paddingY={3}>
                <Text textStyle="sectionTitle" color="Text.highEmphasis">
                  {t('page:biddingIntraday.executionResult.summary.title')}
                  <Text
                    as="span"
                    marginLeft={2}
                    color="Text.lowEmphasis"
                    fontSize="sm"
                  >
                    {t('page:biddingIntraday.executionResult.summary.meta')}
                  </Text>
                </Text>
              </Box>
              {/* 7 列 (エリア / 売買 / 入札件数 / 入札MW / 約定MW / 約定率 /
                加重平均約定価格) の共通コンポーネント (AC-BIN-022b =
                スポット AC-BSP-002b と同一列構成)。 縦スクロール器は持たない
                (= sticky は効かない) が、 7 列が狭い幅で溢れるので横だけ器を置く。 */}
              <Box overflowX="auto">
                <ExecutionAreaSummaryTable
                  rows={summary.rows}
                  total={summary.total}
                  totalFillRateByConventions
                />
              </Box>
            </Box>

            {/* 約定明細 (= 見出し + CSV、 [DS差異]) */}
            <Box
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
              backgroundColor="Surface.primary"
            >
              <Flex alignItems="center" gap={3} paddingX={4} paddingY={3}>
                <Text textStyle="sectionTitle" color="Text.highEmphasis">
                  {t('page:biddingIntraday.executionResult.detail.title')}
                </Text>
                <Spacer />
                <TertiaryButton
                  disabled={detailRows.length === 0}
                  onClick={handleCsvDownload}
                  size="sm"
                >
                  <FaArrowDownToBracketRegular
                    boxSize={iconSize.labeledButton}
                  />
                  {t('page:biddingIntraday.executionResult.csvDownload')}
                </TertiaryButton>
              </Flex>
              <ExecutionDetailTable rows={detailRows} />
            </Box>
          </>
        )}
      </RegionLoading>
    </VStack>
  )
}
